import { useMemo } from "react";

export interface BarSeries {
  key: string;
  label: string;
  /** Nom du token de couleur StyleSystem, ex : "primary-500". */
  token: string;
}

export interface BarGroup {
  key: string;
  label: string;
  values: number[];
}

interface BarChartProps {
  groups: BarGroup[];
  series: BarSeries[];
  valueFormatter: (value: number) => string;
  emptyLabel?: string;
}

/**
 * Histogramme multi-séries responsive (défilement horizontal sur petits écrans).
 * Les couleurs proviennent uniquement des tokens de la charte graphique.
 */
export default function BarChart({
  groups,
  series,
  valueFormatter,
  emptyLabel = "Aucune donnée sur la période sélectionnée.",
}: BarChartProps) {
  const max = useMemo(
    () => groups.reduce((acc, group) => Math.max(acc, ...group.values), 0),
    [groups]
  );
  const hasValues = max > 0;

  if (groups.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-background-300 text-sm text-foreground-500">
        Aucune période à afficher.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        {series.map((item) => (
          <span key={item.key} className="flex items-center gap-2 text-xs text-foreground-600">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: `oklch(var(--${item.token}))` }}
              aria-hidden="true"
            />
            {item.label}
          </span>
        ))}
      </div>

      <div className="flex h-64 items-end gap-2 overflow-x-auto pb-1">
        {groups.map((group) => (
          <div key={group.key} className="flex min-w-[52px] flex-1 flex-col items-center gap-2">
            <div className="flex h-52 w-full items-end justify-center gap-1">
              {series.map((item, index) => {
                const value = group.values[index] ?? 0;
                const pct = hasValues ? Math.round((value / max) * 100) : 0;
                return (
                  <div
                    key={item.key}
                    className="w-3 rounded-t transition-all duration-500"
                    style={{
                      height: `${pct}%`,
                      minHeight: value > 0 ? "4px" : "0",
                      background: `oklch(var(--${item.token}))`,
                    }}
                    title={`${item.label} · ${group.label} : ${valueFormatter(value)}`}
                    aria-hidden="true"
                  />
                );
              })}
            </div>
            <span className="whitespace-nowrap text-[10px] text-foreground-500">{group.label}</span>
          </div>
        ))}
      </div>

      {!hasValues && <p className="text-center text-xs text-foreground-400">{emptyLabel}</p>}
    </div>
  );
}